import React, { useState, useEffect, useRef } from 'react';
import { VStack, HStack, Box, Button, Text, Icon, Checkbox, CheckboxCard, Collapsible, Input, SegmentGroup, Image, Field, Link } from '@chakra-ui/react';
import { LuCircleCheck, LuPenLine, LuLandmark, LuPiggyBank } from 'react-icons/lu';
import { DonationContinueSection } from '../DonationContinueSection';
import { PayPalButtons } from '../PayPalButtons';
import { useFormContext } from '../context/FormContext';
import {
  setPaymentMethod,
  setCardData,
  setBankData,
  setApplePayData,
  setDonorInfo,
  setCoverTheCost,
  nextStep,
  prevStep,
  setSubmitting,
  setReceipt,
  setError,
  setSubmissionError,
} from '../context/formActions';
import { canContinueFromStep, validateBankPaymentField, type BankPaymentFieldErrors } from '../validation';
import { openAuthorizeNetModal, cleanupAuthorizeNet, getAuthorizeNetEnvironment } from '../authorize';
import { prepareApplePayPaymentRequest, startApplePaySession, completeApplePaySession } from '../applePayHelper';
import { logger, donateEvent, purchaseEvent } from '@kuow/core';
import type { CardData, BankData } from '../types';
import type { ApplePayPaymentAuthorizedEvent } from '../applePayTypes';
import sampleCheck from '../../../assets/images/sample-check.png';
import { submitBillingUpdate, submitBillingUpdateWithAuth, submitDonation, submitDonationUpgrade } from '../services/donationSubmission';
import { buildPaymentMethodOptions } from './paymentMethodOptions';
import { useProcessingMessage } from '../../../hooks/useProcessingMessage';
import { useDonationFormUi } from '../context/UiContext';
import {
  getPrimarySubmissionError,
  getSubmissionErrorMessage,
  SUBMISSION_ERROR_FALLBACK_MESSAGE,
} from '../utils/submissionErrors';

export const PaymentStep: React.FC = () => {
  const { state, dispatch, paymentOverrides } = useFormContext();
  const { showStepNavigator, setIsAuthNetOpen } = useDonationFormUi();
  const { formData, formDetails, validation, environment, isSubmitting, canMakeApplePay, submissionError } = state;

  const [bankFormErrors, setBankFormErrors] = useState<BankPaymentFieldErrors>({});
  const [isLoadingCardModal, setIsLoadingCardModal] = useState(false);
  const isPaypalRest = formData.paymentMethod === 'paypal';
  const latestState = useRef(state);
  latestState.current = state;
  const processingMessage = useProcessingMessage(isSubmitting);

  useEffect(() => {
    donateEvent('Step Payment', state.placement);
  }, [state.placement]);

  // Initialize bankData if not present and payment method is bank
  useEffect(() => {
    if (formData.paymentMethod === 'bank' && !formData.bankData) {
      dispatch(setBankData({
        accountType: 'checking',
        firstName: '',
        lastName: '',
        bankName: '',
        routingNumber: '',
        accountNumber: '',
      }));
    }
  }, [formData.paymentMethod, formData.bankData, dispatch]);

  if (!formDetails) {
    return null;
  }

  const bankData = formData.bankData ?? {
    accountType: 'checking' as const,
    firstName: '',
    lastName: '',
    bankName: '',
    routingNumber: '',
    accountNumber: '',
  };

  // Auto-open Authorize.Net modal when card payment method is selected (but not if card data already exists)
  const isCreditCardEnabled = paymentOverrides?.creditCard ?? true;

  const paymentMethods = buildPaymentMethodOptions({
    paymentOverrides,
    formDetails,
    mode: state.mode,
    canMakeApplePay,
    isMonthly: formData.frequency === 'monthly',
  });
  // Single source of truth for whether the promise option is currently offered, derived
  // from the built list rather than recomputed, so the clearing effect below can never
  // drift out of sync with buildPaymentMethodOptions's actual enable rule.
  const canOfferBillLater = paymentMethods.some((method) => method.id === 'bill-later');

  useEffect(() => {
    if (!isCreditCardEnabled) {
      return;
    }

    if (formData.paymentMethod === 'card' && !formData.cardData && !isLoadingCardModal) {
      handleOpenCardModal();
    }
  }, [formData.paymentMethod, formData.cardData, isLoadingCardModal, isCreditCardEnabled]);

  useEffect(() => {
    if (!isCreditCardEnabled && formData.paymentMethod === 'card') {
      dispatch(setPaymentMethod(''));
      dispatch(setCardData(null));
    }
  }, [isCreditCardEnabled, formData.paymentMethod, dispatch]);

  // Clear a stale promise selection if the donor switches to monthly after selecting it
  // (or the override is otherwise withdrawn) — mirrors the credit-card clearing effect above.
  useEffect(() => {
    if (!canOfferBillLater && formData.paymentMethod === 'bill-later') {
      dispatch(setPaymentMethod(''));
    }
  }, [canOfferBillLater, formData.paymentMethod, dispatch]);

  // Cleanup Authorize.Net resources on unmount
  useEffect(() => {
    return () => {
      cleanupAuthorizeNet();
      setIsAuthNetOpen(false);
    };
  }, [setIsAuthNetOpen]);

  const handleOpenCardModal = () => {
    setIsLoadingCardModal(true);
    setIsAuthNetOpen(true);
    const authNetEnvironment = getAuthorizeNetEnvironment(environment);
    
    void openAuthorizeNetModal(
      { environment: authNetEnvironment },
      (cardData: CardData) => {
        // Success callback
        dispatch(setCardData(cardData));
        setIsLoadingCardModal(false);
        setIsAuthNetOpen(false);
      },
      () => {
        // Error callback - clear the payment method so user can try again
        dispatch(setPaymentMethod(''));
        dispatch(setCardData(null));
        setIsLoadingCardModal(false);
        setIsAuthNetOpen(false);
      },
      () => {
        // Dismiss callback - user closed modal without submitting
        dispatch(setPaymentMethod(''));
        dispatch(setCardData(null));
        setIsLoadingCardModal(false);
        setIsAuthNetOpen(false);
      }
    );
  };

  const handlePaymentMethodChange = (method: string) => {
    dispatch(setSubmissionError(null));
    dispatch(setPaymentMethod(method));
  };

  const handleEditCardInfo = () => {
    dispatch(setSubmissionError(null));
    // Clear existing card data and reopen modal
    dispatch(setCardData(null));
    handleOpenCardModal();
  };

  const handleBankFormChange = (field: keyof BankData, value: string) => {
    if (submissionError) {
      dispatch(setSubmissionError(null));
    }
    dispatch(setBankData({ ...bankData, [field]: value }));
    // Clear error for this field when user starts typing
    if (field !== 'accountType' && bankFormErrors[field as keyof BankPaymentFieldErrors]) {
      setBankFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleBankFormBlur = (field: keyof BankData) => {
    if (field !== 'accountType') {
      const error = validateBankPaymentField(field, bankData[field]);
      setBankFormErrors((prev) => ({ ...prev, [field]: error }));
    }
  };

  const handleCoverTheCostChange = (checked: boolean) => {
    if (checked) {
      donateEvent('Select Cover Cost', state.placement);
    }
    dispatch(setCoverTheCost(checked));
  };

  const handleApplePayClick = async () => {
    if (latestState.current.isSubmitting || latestState.current.formData.paypalCompletionUncertain) return;
    logger.info('Apple Pay button clicked');
    dispatch(setSubmissionError(null));
    
    // Set payment method to apple-pay
    dispatch(setPaymentMethod('apple-pay'));
    
    // Set Apple Pay status to initializing
    dispatch(setApplePayData({
      token: null,
      status: 'initializing',
    }));

    try {
      // Prepare payment request
      const donationData = {
        amount: formData.amount ?? formData.customAmount ?? 0,
        amountWithCoverCost: formData.totalDonationAmount ?? 0,
        monthly: formData.frequency === 'monthly',
        coverCost: formData.coverTheCost,
        coverCostAmount: formData.coverTheCostAmount,
        shippingRequired: formData.gift !== null && !formData.noGift,
      };

      const request = prepareApplePayPaymentRequest(donationData);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const session = await startApplePaySession(request);

      if (session) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        session.onpaymentauthorized = (event: ApplePayPaymentAuthorizedEvent) => {
          void handleApplePayAuthorized(event);
        };

        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        session.oncancel = () => {
          logger.info('Apple Pay session cancelled by user');
          // Clear payment method on cancel
          dispatch(setPaymentMethod(''));
          dispatch(setApplePayData(null));
        };
      }
    } catch (error) {
      logger.error('Apple Pay session failed', { error });
      // Clear payment method on error
      dispatch(setPaymentMethod(''));
      dispatch(setApplePayData({
        token: null,
        status: 'error',
      }));
    }
  };

  const handleApplePayAuthorized = async (
    event: ApplePayPaymentAuthorizedEvent
  ) => {
    if (latestState.current.isSubmitting || latestState.current.formData.paypalCompletionUncertain) {
      completeApplePaySession(false);
      return;
    }
    logger.info('Apple Pay payment authorized');

    const payment = event.payment;
    const token = payment.token;
    const billingContact = payment.billingContact;
    const shippingContact = payment.shippingContact;

    // Set Apple Pay data with token and authorized status
    dispatch(setApplePayData({
      token,
      status: 'authorized',
    }));

    // Auto-fill donor information from Apple Pay
    const donorInfo = {
      firstName: billingContact?.givenName ?? '',
      lastName: billingContact?.familyName ?? '',
      email: shippingContact?.emailAddress ?? '',
      phone: billingContact?.phoneNumber ?? '',
      street: shippingContact?.addressLines?.[0] ?? '',
      city: shippingContact?.locality ?? '',
      state: shippingContact?.administrativeArea ?? '',
      country: shippingContact?.country ?? 'United States',
      postalCode: shippingContact?.postalCode ?? '',
      newsletterOptIn: false,
      captcha: '',
    };
    
    dispatch(setDonorInfo(donorInfo));
    dispatch(setSubmitting(true));

    // Submit donation immediately
    try {
      if (!formDetails) {
        throw new Error('Form details not available');
      }

      // Create updated formData with the donor info
      const updatedFormData = {
        ...formData,
        ...donorInfo,
        applePayData: { token, status: 'authorized' as const },
        paymentMethod: 'apple-pay',
      };

      let result;
      
      if (state.mode === 'billing-update') {
        const billingDonation = state.activeSubscription && 'receipt_id' in state.activeSubscription 
          ? state.activeSubscription 
          : null;

        if (!billingDonation) {
          throw new Error('Missing billing update details');
        }

        if (!state.billingUpdateToken && !state.billingUpdateWithAuth) {
          throw new Error('Missing billing update credentials');
        }

        result = await (state.billingUpdateWithAuth
          ? submitBillingUpdateWithAuth(updatedFormData, billingDonation, state.utmCampaign)
          : submitBillingUpdate(updatedFormData, billingDonation, state.billingUpdateToken!, state.utmCampaign));
      } else if (state.mode === 'monthly-upgrade') {
        if (!state.activeSubscription || !state.upgradeJwt) {
          throw new Error('Missing active subscription details');
        }

        result = await submitDonationUpgrade(
          updatedFormData,
          formDetails,
          state.activeSubscription,
          state.upgradeJwt,
          state.useNewPaymentMethod,
          state.utmCampaign
        );
      } else {
        result = await submitDonation(updatedFormData, formDetails, state.utmCampaign, {
          memberId: state.memberId,
          withAuth: Boolean(state.memberId),
          sfmcSubkey: state.sfmcSubkey,
        });
      }

      // Complete Apple Pay session
      completeApplePaySession(result.success);

      if (result.success) {
        // Handle PayPal redirect (shouldn't happen with Apple Pay, but for completeness)
        if (result.redirectUrl) {
          window.location.href = result.redirectUrl;
          return;
        }

        if (result.receipt) {
          const rawAmount = formData.amount ?? formData.customAmount ?? 0;
          const annualizedValue = formData.frequency === 'monthly' ? rawAmount * 12 : rawAmount;
          purchaseEvent(result.receipt.receiptId, annualizedValue, state.placement);

          dispatch(setReceipt(result.receipt));
          return;
        }
      }

      // Handle errors
      const submissionErrorResult = getPrimarySubmissionError(
        result.errors,
        SUBMISSION_ERROR_FALLBACK_MESSAGE
      );
      dispatch(setSubmissionError(submissionErrorResult));
      dispatch(setSubmitting(false));
    } catch (error) {
      logger.error('Apple Pay donation submission failed', { error });
      completeApplePaySession(false);
      dispatch(
        setSubmissionError({
          message:
            error instanceof Error ? error.message : SUBMISSION_ERROR_FALLBACK_MESSAGE,
        })
      );
      dispatch(setSubmitting(false));
    }
  };

  const handleContinue = () => {
    if (!canContinueFromStep('payment', validation)) {
      return;
    }

    if (state.mode !== 'billing-update') {
      dispatch(nextStep());
      return;
    }

    const billingDonation =
      state.activeSubscription && 'receipt_id' in state.activeSubscription ? state.activeSubscription : null;

    if (!billingDonation) {
      dispatch(setError('Missing billing update details'));
      return;
    }

    // Require either updateToken or withAuth
    if (!state.billingUpdateToken && !state.billingUpdateWithAuth) {
      dispatch(setError('Missing billing update credentials'));
      return;
    }

    dispatch(setSubmissionError(null));
    dispatch(setSubmitting(true));

    let submitPromise: ReturnType<typeof submitBillingUpdate>;
    if (state.billingUpdateWithAuth) {
      submitPromise = submitBillingUpdateWithAuth(formData, billingDonation, state.utmCampaign);
    } else {
      const updateToken = state.billingUpdateToken;
      if (!updateToken) {
        dispatch(setError('Missing billing update token'));
        dispatch(setSubmitting(false));
        return;
      }
      submitPromise = submitBillingUpdate(formData, billingDonation, updateToken, state.utmCampaign);
    }

    void submitPromise
      .then((result) => {
        if (result.success) {
          if (result.redirectUrl && formData.paymentMethod === 'paypal') {
            window.location.href = result.redirectUrl;
            return;
          }
          if (result.receipt) {
            const rawAmount = formData.amount ?? formData.customAmount ?? 0;
            const annualizedValue = formData.frequency === 'monthly' ? rawAmount * 12 : rawAmount;
            purchaseEvent(result.receipt.receiptId, annualizedValue, state.placement);

            dispatch(setReceipt(result.receipt));
            return;
          }
        }
        const submissionErrorResult = getPrimarySubmissionError(
          result.errors,
          SUBMISSION_ERROR_FALLBACK_MESSAGE
        );
        dispatch(setSubmissionError(submissionErrorResult));
        dispatch(setSubmitting(false));
      })
      .catch((error: unknown) => {
        logger.error('Failed to submit billing update', { error });
        dispatch(
          setSubmissionError({ message: SUBMISSION_ERROR_FALLBACK_MESSAGE })
        );
        dispatch(setSubmitting(false));
      });
  };

  const currentAmount = formData.totalDonationAmount ?? formData.amount ?? formData.customAmount ?? 0;
  const formattedCoverCostAmount = `$${formData.coverTheCostAmount.toFixed(2)}`;
  const showBackButton = !showStepNavigator;
  const submissionErrorMessage = getSubmissionErrorMessage(submissionError);
  const isBillingUpdate = state.mode === 'billing-update';

  return (
    <VStack gap={6} align="stretch" textAlign="center">
      <Box>
        <Text fontSize="md" fontWeight="semibold" mb={1.5}>
          {isBillingUpdate ? 'Update Payment' : 'Payment'}
        </Text>
        <Text fontSize="xs" color="fg.muted" fontStyle="italic">
          Choose your payment method below.
        </Text>
        {isBillingUpdate ? (
          <Text fontSize="xs" color="fg.muted" fontStyle="italic">
            - or -
            <br />
            <Link
              as="button"
              type="button"
              variant="underline"
              color="river.solid"
              onClick={() => dispatch({ type: 'SET_STEP', payload: 'amount' })}
            >
              Change Amount
            </Link>
          </Text>
        ) : null}
      </Box>
      {submissionErrorMessage ? (
        <Box bg="red.subtle" borderRadius="md" borderWidth={1} borderColor="border" p={3}>
          <Text fontSize="sm" fontWeight="semibold" color="red.fg">
            {submissionErrorMessage}
          </Text>
        </Box>
      ) : null}

      {/* Apple Pay Button - rendered separately as web component */}
      {/* cSpell:disable */}
      {canMakeApplePay && formDetails.apple_pay && !isSubmitting && !formData.paypalCompletionUncertain && (
        <Box
          width="100%"
          height="48px"
          css={{
            '& apple-pay-button': {
              WebkitAppearance: '-apple-pay-button',
              width: '100%',
              height: '48px',
              minHeight: '48px',
              display: 'inline-block',
            }
          }}
        >
          {
            React.createElement('apple-pay-button', {
              onClick: () => { void handleApplePayClick(); },
              buttonstyle: 'black',
              type: 'donate',
              locale: 'en-US',
            })
          }
        </Box>
      )}
      {/* cSpell:enable */}
      
      <VStack gap={3} align="stretch">
        {paymentMethods.map((method) => {
          const isSelected = formData.paymentMethod === method.id;
          const isCard = method.id === 'card';
          const isBank = method.id === 'bank';
          const isPaypal = method.id === 'paypal';
          const isApplePay = method.id === 'apple-pay';

          // Skip Apple Pay here since it's rendered as a separate button above
          if (isApplePay) {
            return null;
          }

          return (
            <Box key={method.id}>
              <CheckboxCard.Root
                checked={isSelected}
                onCheckedChange={() => handlePaymentMethodChange(method.id)}
                variant="outline"
                colorPalette="river"
                justify="center"
                aria-label={method.label}
              >
                <CheckboxCard.HiddenInput />
                <CheckboxCard.Control>
                  <CheckboxCard.Content>
                    <HStack gap={2} w="full" justify="center">
                      {isSelected && <Icon as={LuCircleCheck} boxSize={5} color="river.contrast" />}
                      <Text fontSize="md" fontWeight="medium" color={isSelected ? 'river.contrast' : 'gray.fg'}>
                        {'displayLabel' in method ? method.displayLabel : method.label}
                      </Text>
                      {'logoLight' in method && 'logoDark' in method ? (
                        <Image src={isSelected ? method.logoDark : method.logoLight} alt={method.label} h={5} />
                      ) : (
                        <Icon as={method.icon} boxSize={5} color={isSelected ? 'river.contrast' : 'gray.fg'} />
                      )}
                    </HStack>
                  </CheckboxCard.Content>
                </CheckboxCard.Control>
              </CheckboxCard.Root>

              {isCard && (
                <Collapsible.Root open={isSelected}>
                  <Collapsible.Content>
                    <Box
                      bg="bg.overcast"
                      borderWidth={1}
                      borderColor="border"
                      borderTop="none"
                      borderBottomRadius="lg"
                      px={3}
                      py={4}
                      mt={-1}
                    >
                      <VStack gap={2} align="stretch">
                        {formData.cardData ? (
                          <>
                            <Text fontSize="md" fontWeight="medium" color="fg" textAlign="center">
                              Card Ending in {formData.cardData.cardNumber.slice(-4)}
                            </Text>
                            <Text fontSize="xs" color="fg.muted" textAlign="center">
                              Click the button below to edit your card info.
                            </Text>
                            <Box display="flex" justifyContent="center" pt={1}>
                              <Button
                                size="xs"
                                variant="outline"
                                onClick={handleEditCardInfo}
                                colorPalette="gray"
                                bg="bg"
                              >
                                <Icon as={LuPenLine} mr={1} />
                                Edit Card Info
                              </Button>
                            </Box>
                          </>
                        ) : (
                          <>
                            <Text fontSize="md" fontWeight="medium" color="fg" textAlign="center">
                              {isLoadingCardModal ? 'Loading payment form...' : 'Card Selected'}
                            </Text>
                            <Text fontSize="xs" color="fg.muted" textAlign="center">
                              {isLoadingCardModal 
                                ? 'Please wait while we load the secure payment form.'
                                : 'The secure payment form should open automatically.'}
                            </Text>
                            {!isLoadingCardModal && (
                              <Box display="flex" justifyContent="center" pt={1}>
                                <Button
                                  size="xs"
                                  variant="outline"
                                  onClick={handleOpenCardModal}
                                  colorPalette="gray"
                                  bg="bg"
                                >
                                  <Icon as={LuPenLine} mr={1} />
                                  Enter Card Info
                                </Button>
                              </Box>
                            )}
                          </>
                        )}
                      </VStack>
                    </Box>
                  </Collapsible.Content>
                </Collapsible.Root>
              )}

              {isBank && (
                <Collapsible.Root open={isSelected}>
                  <Collapsible.Content>
                    <Box
                      bg="white"
                      borderWidth={1}
                      borderColor="border"
                      borderTop="none"
                      borderBottomRadius="lg"
                      p={3}
                      mt={-1}
                    >
                      <VStack gap={4} align="stretch">
                        <Box>
                          <Text fontSize="sm" fontWeight="semibold" mb={2} textAlign="center">
                            Account Type
                          </Text>
                          <SegmentGroup.Root
                            value={bankData.accountType}
                            onValueChange={(details) => handleBankFormChange('accountType', details.value as 'checking' | 'savings')}
                            size="sm"
                            colorPalette="river"
                            w="full"
                          >
                            <SegmentGroup.Indicator />
                            <SegmentGroup.Item value="checking" flex={1}>
                              <SegmentGroup.ItemText>
                                <HStack gap={2}>
                                  <Icon as={LuLandmark} boxSize={4} />
                                  <Text as="span">Checking</Text>
                                </HStack>
                              </SegmentGroup.ItemText>
                              <SegmentGroup.ItemHiddenInput />
                            </SegmentGroup.Item>
                            <SegmentGroup.Item value="savings" flex={1}>
                              <SegmentGroup.ItemText>
                                <HStack gap={2}>
                                  <Icon as={LuPiggyBank} boxSize={4} />
                                  <Text as="span">Savings</Text>
                                </HStack>
                              </SegmentGroup.ItemText>
                              <SegmentGroup.ItemHiddenInput />
                            </SegmentGroup.Item>
                          </SegmentGroup.Root>
                        </Box>

                        <HStack gap={3}>
                          <Field.Root flex={1} invalid={!!bankFormErrors.firstName}>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="fg">
                              First Name
                            </Field.Label>
                            <Input
                              value={bankData.firstName}
                              onChange={(e) => handleBankFormChange('firstName', e.target.value)}
                              onBlur={() => handleBankFormBlur('firstName')}
                              size="sm"
                            />
                            {bankFormErrors.firstName && (
                              <Field.ErrorText fontSize="xs">{bankFormErrors.firstName}</Field.ErrorText>
                            )}
                          </Field.Root>
                          <Field.Root flex={1} invalid={!!bankFormErrors.lastName}>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="fg">
                              Last Name
                            </Field.Label>
                            <Input
                              value={bankData.lastName}
                              onChange={(e) => handleBankFormChange('lastName', e.target.value)}
                              onBlur={() => handleBankFormBlur('lastName')}
                              size="sm"
                            />
                            {bankFormErrors.lastName && (
                              <Field.ErrorText fontSize="xs">{bankFormErrors.lastName}</Field.ErrorText>
                            )}
                          </Field.Root>
                        </HStack>

                        <Field.Root invalid={!!bankFormErrors.bankName}>
                          <Field.Label fontSize="sm" fontWeight="semibold" color="fg">
                            Bank Name
                          </Field.Label>
                          <Input
                            value={bankData.bankName}
                            onChange={(e) => handleBankFormChange('bankName', e.target.value)}
                            onBlur={() => handleBankFormBlur('bankName')}
                            size="sm"
                          />
                          {bankFormErrors.bankName && (
                            <Field.ErrorText fontSize="xs">{bankFormErrors.bankName}</Field.ErrorText>
                          )}
                        </Field.Root>

                        <HStack gap={3}>
                          <Field.Root flex={1} invalid={!!bankFormErrors.routingNumber}>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="fg">
                              Routing Number
                            </Field.Label>
                            <Input
                              value={bankData.routingNumber}
                              onChange={(e) => handleBankFormChange('routingNumber', e.target.value)}
                              onBlur={() => handleBankFormBlur('routingNumber')}
                              size="sm"
                              maxLength={9}
                            />
                            {bankFormErrors.routingNumber && (
                              <Field.ErrorText fontSize="xs">{bankFormErrors.routingNumber}</Field.ErrorText>
                            )}
                          </Field.Root>
                          <Field.Root flex={1} invalid={!!bankFormErrors.accountNumber}>
                            <Field.Label fontSize="sm" fontWeight="semibold" color="fg">
                              Account Number
                            </Field.Label>
                            <Input
                              value={bankData.accountNumber}
                              onChange={(e) => handleBankFormChange('accountNumber', e.target.value)}
                              onBlur={() => handleBankFormBlur('accountNumber')}
                              size="sm"
                            />
                            {bankFormErrors.accountNumber && (
                              <Field.ErrorText fontSize="xs">{bankFormErrors.accountNumber}</Field.ErrorText>
                            )}
                          </Field.Root>
                        </HStack>

                        <Box>
                          <Image
                            src={sampleCheck}
                            alt="Sample check showing where to find routing and account numbers"
                            w="full"
                            borderRadius="md"
                          />
                        </Box>
                      </VStack>
                    </Box>
                  </Collapsible.Content>
                </Collapsible.Root>
              )}

              {isPaypal && (
                <Collapsible.Root open={isSelected}>
                  <Collapsible.Content>
                    <Box
                      bg="bg.overcast"
                      borderWidth={1}
                      borderColor="border"
                      borderTop="none"
                      borderBottomRadius="lg"
                      px={3}
                      py={4}
                      mt={-1}
                    >
                      {isPaypalRest && isSelected ? (
                        <PayPalButtons clientId={formDetails.paypal_client_id ?? ''} />
                      ) : null}
                    </Box>
                  </Collapsible.Content>
                </Collapsible.Root>
              )}
            </Box>
          );
        })}
      </VStack>

      <Checkbox.Root
        checked={formData.coverTheCost}
        onCheckedChange={(details) => handleCoverTheCostChange(details.checked === true)}
      >
        <Checkbox.HiddenInput />
        <Checkbox.Control />
        <Checkbox.Label>
          <Text fontSize="xs" textAlign="left" lineHeight="1.2">
            I&apos;d like to help by covering the transaction fees of {formattedCoverCostAmount} for my donation.
          </Text>
        </Checkbox.Label>
      </Checkbox.Root>

      {state.mode === 'monthly-upgrade' && isPaypalRest && (
        <Text fontSize="sm" fontStyle="italic">
          Review your new payment schedule in PayPal before approving. PayPal requires the first payment to be at least 24 hours and 15 minutes away.
        </Text>
      )}

      {state.mode === 'billing-update' && (
        <Text fontSize="sm" fontStyle="italic">
          {isPaypalRest
            ? 'Review your payment schedule in PayPal before approving. Your existing billing date is requested; PayPal requires the first payment to be at least 24 hours and 15 minutes away.'
            : <>Please note that the first payment on your updated pledge will be <strong>processed today</strong>. Future payments will be processed on this same date each month.</>}
        </Text>
      )}

      {!isPaypalRest && <DonationContinueSection
        amount={currentAmount}
        frequency={formData.frequency}
        buttonLabel={isSubmitting ? 'Processing...' : state.mode === 'billing-update' ? 'Submit Update' : 'Continue'}
        onClick={handleContinue}
        disabled={isSubmitting || !canContinueFromStep('payment', validation)}
        loading={isSubmitting}
        loadingText={processingMessage}
        showArrow={state.mode !== 'billing-update'}
      />}

      {showBackButton ? (
        <Button
          variant="subtle"
          size="xs"
          onClick={() => dispatch(prevStep())}
          alignSelf="center"
        >
          Back
        </Button>
      ) : null}

    </VStack>
  );
};
